import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Section, Eyebrow, Heading } from "../components/site/Section";
import {
  getAdminKey, setAdminKey, clearAdminKey,
  adminList, adminCreate, adminUpdate, adminDelete, adminResetWeek,
  NATIONS_LIST, RARITY_STYLES,
} from "../lib/archive";
import { useAuth } from "../lib/auth";

const EMPTY = {
  name: "", nation: "verdani", title: "", quote: "",
  bio: "", abilities: "", image: "", image_position: "center", rarity: "C",
  unlock_method: "guess", unlock_code: "",
};

export default function ArchiveAdminPage() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [authed, setAuthed] = useState(Boolean(getAdminKey()) || isOwner);
  const [keyInput, setKeyInput] = useState("");
  const [chars, setChars] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  // If the owner logs in after this component mounted, auto-authenticate.
  useEffect(() => {
    if (isOwner && !authed) setAuthed(true);
  }, [isOwner, authed]);

  const reload = async () => {
    try { setChars(await adminList()); }
    catch {
      if (isOwner) { toast.error("Failed to load archive."); }
      else { toast.error("Auth failed. Re-enter your admin key."); clearAdminKey(); setAuthed(false); }
    }
  };

  useEffect(() => { if (authed) reload(); }, [authed]);

  const login = () => {
    if (!keyInput) return;
    setAdminKey(keyInput.trim());
    setAuthed(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required."); return; }
    try {
      if (editingId) {
        await adminUpdate(editingId, form);
        toast.success("Character updated.");
      } else {
        await adminCreate(form);
        toast.success("Character added to the archive.");
      }
      setForm(EMPTY);
      setEditingId(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  const edit = (c) => { setForm({ ...EMPTY, ...c }); setEditingId(c.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (c) => {
    if (!confirm(`Delete ${c.name}?`)) return;
    try { await adminDelete(c.id); toast.success("Deleted."); reload(); }
    catch { toast.error("Delete failed."); }
  };
  const reroll = async () => {
    if (!confirm("Force a new weekly drop now?")) return;
    try { await adminResetWeek(); toast.success("Week reset — next page load draws a new character."); }
    catch { toast.error("Reroll failed."); }
  };

  if (!authed) {
    return (
      <Section testId="admin-login" className="pt-16 md:pt-24 max-w-md mx-auto">
        <Eyebrow>Restricted</Eyebrow>
        <Heading className="mb-6">Archive Admin</Heading>
        <form
          onSubmit={(e) => { e.preventDefault(); login(); }}
          className="bg-[#0A0A0A] border border-[#C5A059]/25 p-6"
        >
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">Admin Key</label>
          <input
            type="password"
            autoFocus
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            className="mt-3 w-full bg-black border border-white/15 px-4 py-3 text-[#EAE0D5] focus:outline-none focus:border-[#C5A059]"
            data-testid="admin-key-input"
          />
          <button
            type="submit"
            className="mt-4 bg-[#C5A059] text-black px-6 py-3 text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF68] w-full"
            data-testid="admin-login-btn"
          >
            Enter
          </button>
        </form>
      </Section>
    );
  }

  return (
    <div data-testid="admin-page">
      <Section testId="admin-hero" className="pt-12 md:pt-24">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Eyebrow>Archive Admin</Eyebrow>
            <Heading>Hidden characters.</Heading>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/admin/stats"
              data-testid="admin-nav-stats"
              className="text-xs tracking-[0.22em] uppercase border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 px-4 py-2"
            >
              Live stats
            </Link>
            <button
              type="button"
              onClick={reroll}
              className="text-xs tracking-[0.22em] uppercase border border-white/10 text-[#A39E99] hover:text-[#EAE0D5] px-4 py-2"
            >
              Force new drop
            </button>
            <button
              type="button"
              onClick={() => { clearAdminKey(); if (!isOwner) setAuthed(false); }}
              className="text-xs tracking-[0.22em] uppercase border border-white/10 text-[#A39E99] hover:text-[#EAE0D5] px-4 py-2"
            >
              {isOwner ? "Clear key" : "Log out"}
            </button>
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        <form
          onSubmit={submit}
          className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 grid md:grid-cols-2 gap-5"
          data-testid="admin-form"
        >
          <div className="md:col-span-2 text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">
            {editingId ? "Edit Character" : "Add New Character"}
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Name *</label>
            <input className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="form-name" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Nation *</label>
            <select className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.nation} onChange={e => setForm({...form, nation: e.target.value})} data-testid="form-nation">
              {NATIONS_LIST.map(n => <option key={n.slug} value={n.slug}>{n.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Title</label>
            <input className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="form-title" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Rarity *</label>
            <select className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.rarity} onChange={e => setForm({...form, rarity: e.target.value})} data-testid="form-rarity">
              {["C","B","A","S"].map(r => <option key={r} value={r}>{RARITY_STYLES[r].label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Quote</label>
            <input className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.quote} onChange={e => setForm({...form, quote: e.target.value})} data-testid="form-quote" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Biography</label>
            <textarea rows={4} className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} data-testid="form-bio" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Abilities</label>
            <input className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.abilities} onChange={e => setForm({...form, abilities: e.target.value})} placeholder="Sep with ·" data-testid="form-abilities" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Artwork URL or /art/file.jpg</label>
            <input className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.image} onChange={e => setForm({...form, image: e.target.value})} placeholder="/art/character.jpg or https://…" data-testid="form-image" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">
              Image Position <span className="text-[#737373] normal-case">— CSS object-position. E.g. <code>center</code>, <code>top</code>, <code>50% 20%</code> (shows upper area).</span>
            </label>
            <input className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.image_position} onChange={e => setForm({...form, image_position: e.target.value})} placeholder="center" data-testid="form-image-position" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">Unlock Method</label>
            <select className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5]" value={form.unlock_method} onChange={e => setForm({...form, unlock_method: e.target.value})} data-testid="form-unlock-method">
              <option value="guess">Weekly drop (guess the nation)</option>
              <option value="code">Code only (excluded from weekly draws)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#A39E99] mb-2">
              Unlock Code {form.unlock_method === "code" && <span className="text-[#C5A059]">· required</span>}
            </label>
            <input
              className="w-full bg-black border border-white/10 px-3 py-2 text-[#EAE0D5] uppercase tracking-[0.18em]"
              value={form.unlock_code}
              onChange={e => setForm({...form, unlock_code: e.target.value})}
              placeholder="EXAMPLE-CODE-2026"
              disabled={form.unlock_method !== "code"}
              data-testid="form-unlock-code"
            />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="bg-[#C5A059] text-black px-6 py-3 text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF68]" data-testid="form-submit">
              {editingId ? "Update Character" : "Add to Archive"}
            </button>
            {editingId && (
              <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="border border-white/10 text-[#A39E99] px-6 py-3 text-xs tracking-[0.22em] uppercase">
                Cancel
              </button>
            )}
          </div>
        </form>
      </Section>

      <Section className="pt-0">
        <Eyebrow>All Characters ({chars.length})</Eyebrow>
        <div className="mt-6 space-y-3" data-testid="admin-list">
          {chars.map((c) => (
            <div key={c.id} className="bg-[#0A0A0A] border border-white/5 p-4 md:p-5 flex items-center gap-4">
              <div className="w-14 h-14 shrink-0 bg-[#050505] overflow-hidden">
                {c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#EAE0D5] font-display text-lg">{c.name}</span>
                  <span className="text-[10px] tracking-[0.22em] uppercase" style={{ color: RARITY_STYLES[c.rarity]?.color }}>{c.rarity}</span>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">{c.nation}</span>
                  {c.unlocked ? (
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#5BAE5B]">Unlocked</span>
                  ) : (
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#A39E99]">Locked</span>
                  )}
                </div>
                <div className="text-xs text-[#737373] mt-1 truncate">{c.title}</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setPreviewing(c)} className="text-xs tracking-[0.2em] uppercase border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 px-3 py-1.5" data-testid={`admin-preview-${c.id}`}>Preview</button>
                <button type="button" onClick={() => edit(c)} className="text-xs tracking-[0.2em] uppercase border border-white/10 text-[#A39E99] hover:text-[#EAE0D5] px-3 py-1.5">Edit</button>
                <button type="button" onClick={() => del(c)} className="text-xs tracking-[0.2em] uppercase border border-white/10 text-[#A8323A] hover:bg-[#A8323A]/10 px-3 py-1.5">Delete</button>
              </div>
            </div>
          ))}
          {chars.length === 0 && (
            <div className="border border-dashed border-white/10 p-8 text-center text-[#A39E99]">
              No characters yet. Add the first record above.
            </div>
          )}
        </div>
      </Section>
      {previewing && <PreviewModal character={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

function PreviewModal({ character, onClose }) {
  const c = character || {};
  const r = RARITY_STYLES[c.rarity] || RARITY_STYLES.C;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-preview-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-[#C5A059]/30 max-w-6xl w-full my-8 md:my-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">
            Preview · {c.name}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-preview-close"
            className="text-[#A39E99] hover:text-[#EAE0D5] text-xs tracking-[0.22em] uppercase"
          >
            Close ✕
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-white/5">
          {/* LOCKED VIEW (what visitors see before guessing) */}
          <div className="bg-[#0A0A0A] p-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99] mb-3">Locked Record (public)</div>
            <div className="aspect-[4/5] bg-[#050505] relative overflow-hidden">
              {c.image ? (
                <img
                  src={c.image}
                  alt="locked"
                  className="w-full h-full object-cover"
                  style={{
                    filter: "blur(14px) saturate(0.55) brightness(0.55) contrast(1.15)",
                    transform: "scale(1.08)",
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#737373] text-xs">SILHOUETTE</div>
              )}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%), linear-gradient(180deg, rgba(168,50,58,0.10) 0%, rgba(0,0,0,0.4) 100%)",
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center p-4">
                {c.quote ? (
                  <p
                    className="font-display italic text-[#EAE0D5] text-center text-sm md:text-base leading-snug max-w-xs select-none"
                    style={{ textShadow: "0 2px 18px rgba(0,0,0,0.95), 0 0 36px rgba(0,0,0,0.7)" }}
                  >
                    "{c.quote}"
                  </p>
                ) : (
                  <span className="font-display text-7xl text-[#C5A059]/15 select-none">?</span>
                )}
              </div>
              <div className="absolute top-3 left-3 text-[10px] tracking-[0.32em] uppercase text-[#C5A059] bg-black/60 backdrop-blur-sm px-2.5 py-1 border border-[#C5A059]/30">
                Locked Record
              </div>
              <div
                className="absolute top-3 right-3 text-[10px] tracking-[0.22em] uppercase bg-black/60 backdrop-blur-sm px-2 py-1 border"
                style={{ borderColor: `${r.color}66`, color: r.color }}
              >
                ? · {r.label}
              </div>
            </div>
          </div>

          {/* UNLOCKED VIEW (post-guess reveal) */}
          <div className="bg-[#0A0A0A] p-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059] mb-3">Unlocked Reveal</div>
            <div className="aspect-[4/5] bg-[#050505] relative overflow-hidden">
              {c.image ? (
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" style={{ objectPosition: c.image_position || "center" }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#737373] text-xs">NO IMAGE</div>
              )}
              <div
                className="absolute top-3 left-3 inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase backdrop-blur-sm bg-black/50"
                style={{ borderColor: `${r.color}66`, color: r.color }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: r.color }} />
                {r.label}
              </div>
              <div className="absolute top-3 right-3 text-[10px] tracking-[0.22em] uppercase px-2 py-1 bg-black/60 text-[#C5A059] backdrop-blur-sm border border-[#C5A059]/30">
                Archive
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: r.color }}>
                {c.title || "—"}
              </div>
              <div className="flex items-center justify-between mt-1">
                <h3 className="font-display text-2xl text-[#EAE0D5]">{c.name}</h3>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">{c.nation}</span>
              </div>
              {c.quote && (
                <p className="italic text-[#A39E99] mt-3 text-sm leading-relaxed">"{c.quote}"</p>
              )}
              {c.bio && (
                <p className="text-sm text-[#A39E99] mt-3 leading-relaxed">{c.bio}</p>
              )}
              {c.abilities && (
                <>
                  <div className="text-[10px] tracking-[0.22em] uppercase mt-4 pt-3 border-t border-white/5" style={{ color: r.color }}>
                    Abilities
                  </div>
                  <div className="text-xs text-[#A39E99] mt-1.5">{c.abilities}</div>
                </>
              )}
              {c.unlock_method === "code" && (
                <div className="mt-4 border border-[#C5A059]/30 px-3 py-2 text-[10px] tracking-[0.22em] uppercase text-[#C5A059]">
                  Code-Only · {c.unlock_code || "(no code set)"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
